import * as React from "react";
import { useCommonHooks } from "../../hooks";
import { useCrudApi } from "../../hooks/useCrudApi";
import { ActionCode, EditFunctionDto } from "../../api";
import {
  FGTextAreaInput,
  FlexFieldGroup,
  showSuccess,
  useFGFormik,
} from "nsitools-react";
import * as Yup from "yup";
import {
  CardFieldGroup,
  CustomFormGenerator,
  FGTranslations,
} from "../../components";
import FunctionalDirectorSelect from "../../components/selects/FunctionalDirectorSelect";
import FGFileInput from "../../components/formGenerator/FGFileInput";

export interface IFunctionDetailPanelProps {
  functionId: number;
}

export const FunctionDetailPanel: React.FunctionComponent<
  IFunctionDetailPanelProps
> = ({ functionId }) => {
  const { apis, t, hasAction } = useCommonHooks();

  const isEditable = React.useMemo(
    () => hasAction([ActionCode.EditFunction]),
    [hasAction]
  );

  const { data, isFetching, validationErrors, onSave, isSaving, refetch } =
    useCrudApi({
      queryKey: ["function-details", functionId],
      fetchFn: async () => {
        if (functionId > 0) {
          return apis.functions.get(functionId);
        }
      },
      saveFn: async (toSave: EditFunctionDto) => {
        return apis.functions.save(toSave);
      },
      onSaved: () => {
        showSuccess(t("FunctionSaved"));
        refetch();
      },
      serverValidationRootKey: "Dto",
    });

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        functionalDirectorId: Yup.number().nullable().required(t("Required")),
      }),
    [t]
  );

  const formik = useFGFormik({
    initialValues: data,
    onSubmit: (d) => onSave(d),
    validationSchema: validationSchema,
  });

  return (
    <FlexFieldGroup columns={2}>
      <CardFieldGroup title={t("Details")}>
        <CustomFormGenerator
          initialValues={data}
          loading={isFetching}
          onSubmit={onSave}
          saving={isSaving}
          editable={isEditable}
          editMode={isEditable}
          showDeleteButton={false}
          validationErrors={validationErrors}
          validationSchema={validationSchema}
          formik={formik}
        >
          <FGTranslations label={t("Name")} />
          <FunctionalDirectorSelect name="functionalDirectorId" />
          <FGFileInput
            name="file"
            label={t("DescriptionFile")}
            downloadFn={() =>
              apis.dossierDocuments.downloadFile(formik?.values?.sharepointUrl)
            }
          />
          <FGTextAreaInput name="note" label={t("Note")} rows={7} />
        </CustomFormGenerator>
      </CardFieldGroup>
    </FlexFieldGroup>
  );
};

export default FunctionDetailPanel;
